import { useCallback, useEffect, useRef } from 'react';
export function useSlide({ scrollRef, childrenKeys, onRangeEnd, onClick }) {
    const childrenRef = useRef([]);
    const lastInitialChildRef = useRef(undefined);
    const lastPositionKeyRef = useRef(undefined);
    const lastPositionOffsetRef = useRef(0);
    /**
     * Make sure childrenRef is always updated and recalculated with new children (if they will change).
     */
    useEffect(() => {
        if (!scrollRef.current)
            return undefined;
        childrenRef.current = Array.from(scrollRef.current.children).map((child, index) => ({
            index,
            key: childrenKeys[index],
            absCenter: child.offsetLeft + child.clientWidth / 2,
            relCenter: child.offsetLeft + child.clientWidth / 2 - (scrollRef.current?.offsetLeft ?? 0),
        }));
    }, [scrollRef, childrenKeys]);
    /**
     * Finds and returns Child whose CENTER is closest to the CENTER of the scroll viewport.
     */
    const findClosestChild = useCallback((positionX) => {
        if (!scrollRef.current)
            return { closestChild: undefined, offset: 0 };
        const scrollContainer = scrollRef.current;
        const children = Array.from(scrollContainer.children);
        // Calc real position X.
        if (!positionX || typeof positionX === 'undefined') {
            // When positionX is not privded - use scrollContainer center point.
            positionX = scrollContainer.offsetLeft + scrollContainer.scrollLeft + scrollContainer.clientWidth / 2;
        }
        else {
            // Otherwise add scroll to position.
            positionX =
                scrollContainer.scrollLeft +
                    positionX -
                    (scrollRef.current.getBoundingClientRect().left - scrollContainer.offsetLeft);
        }
        let closestChild = undefined;
        let closestChildOffset = 0;
        let minDifference = Infinity;
        for (let index = 0; index < children.length; index++) {
            const difference = childrenRef.current[index].absCenter - positionX;
            const absDifference = Math.abs(difference);
            if (absDifference < minDifference) {
                minDifference = absDifference;
                closestChild = childrenRef.current[index];
                closestChildOffset = difference;
            }
        }
        return { closestChild, offset: closestChildOffset };
    }, [scrollRef]);
    const setLastPosition = useCallback((child, offset = 0) => {
        lastPositionKeyRef.current = child.key;
        lastPositionOffsetRef.current = offset;
        if (child.index <= 5 || child.index >= childrenRef.current.length - 5) {
            onRangeEnd(child.index);
        }
    }, [onRangeEnd]);
    /**
     * Internal helper function that centralize sole scrolling action.
     */
    const scrollTo = useCallback((toPosition, instantly = false) => {
        if (!scrollRef.current)
            return;
        scrollRef.current.scrollTo({
            left: toPosition,
            behavior: instantly ? 'instant' : 'smooth',
        });
    }, [scrollRef]);
    /**
     * Scrolls to make this child put in the center of the scroll.
     */
    const scrollToChild = useCallback((toChild, offset = 0, instantly = false) => {
        if (!scrollRef.current)
            return;
        scrollTo(toChild.relCenter - scrollRef.current.clientWidth / 2 - offset, instantly);
        setTimeout(() => {
            setLastPosition(toChild, offset);
        }, 100);
    }, [scrollRef, scrollTo, setLastPosition]);
    /**
     * When receiving order to scroll to exact key - finds the child and orders `scrollToChild`.
     */
    const scrollToKey = useCallback((toKey, offset = 0, instantly = false) => {
        const child = childrenRef.current.find((child) => child.key === toKey);
        if (child) {
            scrollToChild(child, offset, instantly);
        }
    }, [scrollToChild]);
    /**
     * Render the component (due to changes in days)
     *
     * This will scroll to show initial child (if initial child has changed or there is no last position known.
     */
    const handleRender = useCallback((initialChildKey) => {
        const hasInitialChildChanged = lastInitialChildRef.current !== initialChildKey && initialChildKey;
        const isLastPositionUnknown = !lastPositionKeyRef.current;
        if (initialChildKey && (hasInitialChildChanged || isLastPositionUnknown)) {
            scrollToKey(initialChildKey);
            lastInitialChildRef.current = initialChildKey;
        }
        else if (lastPositionKeyRef.current) {
            scrollToKey(lastPositionKeyRef.current, lastPositionOffsetRef.current, true);
        }
    }, [scrollToKey]);
    /**
     * After dragging it may be needed to "jump" to equal position - this is what this function does when being called after end of drag.
     */
    const identifyChild = useCallback(() => {
        const { closestChild, offset } = findClosestChild();
        if (closestChild) {
            setLastPosition(closestChild, offset);
        }
        return closestChild;
    }, [findClosestChild, setLastPosition]);
    /**
     * Used to scroll by amount of children positive (to the right) or negative (to the left).
     */
    const handleScrollBy = useCallback((dir) => {
        const currIndex = childrenRef.current.findIndex((child) => child.key === lastPositionKeyRef.current);
        const nextIndex = currIndex + dir;
        const nextChild = childrenRef.current[nextIndex] ?? undefined;
        if (nextChild) {
            scrollToChild(nextChild);
        }
    }, [scrollToChild]);
    const handleDragEnd = useCallback((dragEndData) => {
        // Undefined delta is for fake-drag actions (stalled, repeated, etc.) - ignorable.
        if (!dragEndData || typeof dragEndData.delta === 'undefined')
            return;
        // Delta <> 0 is when user dragged the scroller left/right. We need to inform the scroller to reposition after drag.
        if (dragEndData.delta !== 0) {
            identifyChild();
            return;
        }
        // Being here means that delta === 0.
        // That means user only clicked/touched on the scroller - without actually moving it.
        // We will emit `onClick`.
        const { closestChild } = findClosestChild(dragEndData.positionX);
        if (typeof closestChild !== 'undefined') {
            onClick(closestChild.key.slice(2));
        }
    }, [findClosestChild, identifyChild, onClick]);
    return {
        handleRender,
        handleScrollBy,
        handleDragEnd,
    };
}
